/**
 * 地区与学校
 * 
 * @Author: Focci
 * @Date: 2023-07-24 10:36:09
 * @Last Modified by: Focci
 * @Last Modified time: 2023-07-24 10:36:09
 */

'use client'

import PropTypes from 'prop-types'
import clsx from 'clsx'
import useFetch from '@hook/useFetch'
import useMessage from '@hook/useMessage'
import { useTranslations } from 'use-intl'
import { times } from 'lodash-es'
import { Skeleton } from '@comp/material'
import { useState, useCallback, useRef } from 'react'
import SearchDialog from '../SearchDialog'
import ZoneAndSchoolBody from './ZoneAndSchoolBody'

function ZoneAndSchoolSkeleton() {
  return (
    <div className="grid grid-cols-3 w-full">
      <div className="col-span-1 flex flex-col">
        <div className="h-12 flex items-center border-b border-b-solid shrink-0">
          <Skeleton variant="rectangular" className="w-[5rem] h-8 rounded-full" />
        </div>
        <div className="flex-1 mt-4 flex flex-col gap-2">
          {times(5, String).map((_, index) => (
            <Skeleton 
              key={index} 
              variant="rectangular" 
              className="w-full rounded-sm h-4 w-11/12" 
            />
          ))}
        </div>
      </div>
      <div className="col-span-2 flex flex-col">
        <div className="h-12 flex gap-2 items-center border-b border-b-solid pl-4">
          <Skeleton variant="rectangular" className="w-[5rem] h-8" />
          <Skeleton variant="rectangular" className="w-[5rem] h-8" />
        </div>
        <div className="pl-4 mt-4 flex flex-col flex-auto gap-2">
          {times(3, String).map((_, index) => (
            <Skeleton 
              key={index} 
              variant="rectangular" 
              className={clsx(['rounded-sm h-4'])}
              style={{ width: `${((12 - index) / 12) * 100}%` }} 
            />
          ))}
        </div>
      </div>
    </div>
  )
}

export default function ZoneAndSchool({
  onSubmit = () => {},
  single = false, // 是否单选(地区)
  module = 'district,school', // 要显示的模块
  ...dialogProps
}) {
  const bodyRef = useRef()
  const tc = useTranslations('common')
  const { message } = useMessage()
  const { data, isLoading } = useFetch('locations')
  const [type, setType] = useState(module.split(',')[0])

  const handleReset = useCallback(() => {
    bodyRef.current.reset()
  }, [])

  const handleConfirm = useCallback(() => {
    const tmp = bodyRef.current.getCheckedData()
    if(tmp?.data?.length > 0) {
      onSubmit(tmp)
    } else {
      message(tc('selectAtLeastOnePieceOfData'))
    }
  }, [message, onSubmit, tc])
  
  return (
    <SearchDialog 
      onReset={handleReset}
      onConfirm={handleConfirm}
      showAction={!isLoading && type === 'district'}
      {...dialogProps}
      actionButtonProps={{
        ...dialogProps.actionButtonProps,
        confirmLabel: tc('confirm')
      }}
    >
      {isLoading && <ZoneAndSchoolSkeleton />}
      {!isLoading && (
        <ZoneAndSchoolBody
          ref={bodyRef}
          type={type}
          module={module}
          single={single}
          setType={setType} 
          data={data}
          onSchoolSubmit={onSubmit}
        />
      )}
    </SearchDialog>
  )
}

ZoneAndSchool.propTypes = {
  onClose: PropTypes.func,
}
